/**
 * eager: 即时加载，在注册时立即执行 module 方法。
 * idle: 闲时加载，在下一个 requestIdleCallback 时机执行 module 方法。如果浏览器不支持 requestIdleCallback 则会降级为按需加载。
 * on-demand: 按需加载，只有业务在调用 .promise() 时才会执行 module 方法。
 */
export type DynamicLoadType = 'eager' | 'idle' | 'on-demand';

export interface IDynamicServiceConfig<S> {
    name: string;
    loadType: DynamicLoadType;
    module(): Promise<S>;
}

export interface IDynamicService<S> {
    /**
     * 同步获取危险的服务实例。命名为危险实例是因为在引用时无法保证服务已经异步加载完成。
     * 调用方应当在任何时候都偏好使用 `promise` 方法异步获取服务实例。只有当 100% 确定服务已经异步加载完成的同步场景下可以使用此引用。
     * @see promise
     */
    dangerousInstance?: S;
    /**
     * 异步获取服务实例。
     */
    promise(): Promise<S>;
    /**
     * 注册在 service 初始化之后执行的回调。
     * 时机为 `new` service 之后，`promise` 返回之前。
     * 使用场景：
     * 如果异步服务需要保证在初始化后同步执行某些操作，但是又不想即时加载服务，可以调用此方法注册回调。注册的回调会在服务加载完成后，同步按照注册顺序执行。
     * 如果注册回调时服务已经加载完成，则回调会立即执行。
     *
     * ⚠️注意：虽然可以在该方法的回调参数中拿到服务实例，但此方法并不能用来触发服务的加载流程。显式触发服务加载或者获取服务实例应该使用 `promise`方法。
     * @param callback 异步加载完成后，需要同步执行的回调
     */
    onDidInit(callback: (service: S) => void | Promise<void>): void;
}

export class DynamicServiceLoader {
    private static instance?: DynamicServiceLoader;

    public static getInstance(): DynamicServiceLoader {
        if (!this.instance) {
            this.instance = new DynamicServiceLoader();
        }
        return this.instance;
    }

    private dynamicServiceMap = new Map<string, IDynamicService<any>>();

    private moduleCache = new Map<string, any>();

    private serviceDidInitCallbacks = new Map<string, ((service: any) => void)[]>();

    public registerDynamicService<S>(config: IDynamicServiceConfig<S>): IDynamicService<S> {
        if (this.dynamicServiceMap.has(config.name)) {
            window.log?.error?.(`DynamicServiceLoader: ${config.name} service already registered`);
            return this.dynamicServiceMap.get(config.name) as IDynamicService<S>;
        }
        const dynamicService = this.createDynamicServiceByConfig(config);
        this.dynamicServiceMap.set(config.name, dynamicService);
        if (config.loadType === 'eager') {
            dynamicService.promise();
        } else if (config.loadType === 'idle') {
            window.requestIdleCallback?.(() => dynamicService.promise());
        }
        return dynamicService;
    }

    public disposeService(config: IDynamicServiceConfig<any> | string): void {
        const configName = typeof config === 'string' ? config : config.name;
        this.dynamicServiceMap.delete(configName);
        this.moduleCache.delete(configName);
        this.serviceDidInitCallbacks.delete(configName);
    }

    private createDynamicServiceByConfig<S>(config: IDynamicServiceConfig<S>): IDynamicService<S> {
        const dynamicService: IDynamicService<S> = {
            promise: async () => {
                if (this.moduleCache.has(config.name)) {
                    return this.moduleCache.get(config.name) as S;
                }
                const servicePromise = config.module();
                this.moduleCache.set(config.name, servicePromise);
                dynamicService.dangerousInstance = await servicePromise;
                const didInitCallbacks = this.serviceDidInitCallbacks.get(config.name);
                if (didInitCallbacks) {
                    // callback 执行顺序应当与注册顺序一致
                    didInitCallbacks.forEach(callback => callback(dynamicService.dangerousInstance));
                    this.serviceDidInitCallbacks.delete(config.name);
                }
                return servicePromise;
            },
            onDidInit: (callback) => {
                if (dynamicService.dangerousInstance) {
                    // 有赋值说明服务已经加载完成，直接执行要注册的 didInit callback 即可
                    callback(dynamicService.dangerousInstance);
                    return;
                }
                // 暂时还没有加载完成，则先缓存注册的 callbacks，待 promise 执行后再逐一调用
                if (!this.serviceDidInitCallbacks.has(config.name)) {
                    this.serviceDidInitCallbacks.set(config.name, []);
                }
                this.serviceDidInitCallbacks.get(config.name)?.push(callback);
            },
        };
        return dynamicService;
    }
}
